import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import IconButton from "@mui/material/IconButton";
import { enqueueInfoNotification } from "core/redux/actions/notification/notificationActions";
import type { ApplicationState } from "core/redux/reducers/rootReducer";
import type { AppDispatch } from "core/redux/store/configureStore.types";
import theme from "core/utilities/theme";
import I18n from "i18n/en-US";
import { useDispatch, useSelector } from "react-redux";
import type { EntitySequence as Sequence, SequenceType } from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import type { HandleInsertSequences } from "sequenceAnalyser/components/SequenceAnalyser/SequenceAnalyser";
import SequenceTableVisualisationWrapper from "sequenceAnalyser/components/Sequences/SequenceTableVisualisationWrapper/SequenceTableVisualisationWrapper";
import { toggleSequenceAnalyserVisualisation } from "sequenceAnalyser/components/TableActions/TableActions";
import { updateVisualisationsSetting } from "sequenceAnalyser/redux/actions/sequenceAnalyserActions";
import type { ColumnsLetterOccurrences } from "sequenceAnalyser/utilities/colorCalculations/colorCalculations";
import { RESIDUE_PIXEL_WIDTH } from "sequenceAnalyser/utilities/screen/screen";
import { removeAllGaps } from "sequenceAnalyser/utilities/selectionActions/removeGaps";
import {
  createSequenceFromManualInsert,
  generateSequenceQualifiedId,
  SequenceQualifiedIdTypes,
} from "sequenceAnalyser/utilities/selectionActions/selectionActions";
import { SequenceAnalyserVisualisations } from "sequenceAnalyser/utilities/sequenceAnalyser.types";

interface ComponentProps {
  sidebarPixelWidth: number;
  width: number;
  consensusHeight: number;
  sequences: Sequence[];
  onInsertSequences: HandleInsertSequences;
  sessionSequenceType: SequenceType;
  columnsLetterOccurrences: ColumnsLetterOccurrences;
}

function ConsensusVisualization({
  sidebarPixelWidth,
  width,
  consensusHeight,
  sequences,
  onInsertSequences,
  sessionSequenceType,
  columnsLetterOccurrences,
}: ComponentProps) {
  const dispatch = useDispatch<AppDispatch>();
  const settings = useSelector((state: ApplicationState) => state.sequenceAnalyser.settings);

  return (
    <SequenceTableVisualisationWrapper
      title={I18n.SequenceAnalyser.SequenceTableVisualisations.consensus.title}
      sidebarPixelWidth={sidebarPixelWidth}
      width={width}
      height={consensusHeight}
      sidebarActions={
        <>
          <IconButton
            title={I18n.SequenceAnalyser.SequenceTableVisualisations.consensus.insert}
            onClick={() => {
              const consensusResidues = Object.keys(columnsLetterOccurrences)
                .map((id) => {
                  const position = Number(id);
                  const topLetter = Object.keys(columnsLetterOccurrences[position]).find(
                    (letter) => columnsLetterOccurrences[position][letter].top,
                  );
                  return topLetter || ".";
                })
                .join("");

              const { newSequence, newAlignedSequence } = createSequenceFromManualInsert(
                generateSequenceQualifiedId(SequenceQualifiedIdTypes.Consensus, sequences),
                I18n.SequenceAnalyser.SequenceTableVisualisations.consensus.title,
                consensusResidues,
                sessionSequenceType,
              );

              const newRawSequence: Sequence = {
                ...newSequence,
                // Raw sequence shouldn't contain any gap
                residues: removeAllGaps(newSequence.residues),
              };

              // Callback to insert new sequence(s) at the right position
              onInsertSequences([newRawSequence], [newSequence], [newAlignedSequence], sequences.length);

              dispatch(enqueueInfoNotification(I18n.SequenceAnalyser.SequenceTableVisualisations.consensus.success));
            }}
            sx={{ padding: theme.spacing(0.25, 0.5) }}
          >
            <ContentCopyIcon sx={{ fontSize: "14px" }} />
          </IconButton>
          <IconButton
            title={I18n.SequenceAnalyser.SequenceTableVisualisations.consensus.close}
            onClick={() => {
              const newViews = toggleSequenceAnalyserVisualisation(
                SequenceAnalyserVisualisations.consensus,
                settings.visualisations,
              );

              dispatch(updateVisualisationsSetting(newViews));
            }}
            sx={{ padding: theme.spacing(0.25, 0.5) }}
          >
            <CloseIcon sx={{ fontSize: "14px" }} />
          </IconButton>
        </>
      }
    >
      <>
        {Object.keys(columnsLetterOccurrences).map((id) => {
          const position = Number(id);
          const topLetter = Object.keys(columnsLetterOccurrences[position]).find(
            (letter) => columnsLetterOccurrences[position][letter].top,
          );

          return (
            <div
              key={position}
              style={{
                display: "inline-block",
                width: `${RESIDUE_PIXEL_WIDTH}px`,
              }}
              title={`Position: ${position + 1}`}
            >
              {topLetter || "."}
            </div>
          );
        })}
      </>
    </SequenceTableVisualisationWrapper>
  );
}
export default ConsensusVisualization;
